/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { createContext, useEffect, useReducer } from "react";
// import axios, { AxiosError } from "axios";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useIsAuthenticated } from "@azure/msal-react";
import { useDispatch } from "react-redux";
import { getCookie, setCookie, removeCookie } from "cms/auth/cookie";
import { useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import Config from "data/config";
import { LOGIN, LOGOUT } from "data/store/reducers/auth/auth.actions";
import { authReducer } from "data/store/reducers/auth/auth";
import Loader from "components/Loader";
import { removeAccountItem, setAccountItem, setUserRoles } from "data/store/reducers/account/account.slice";
import { setOrganizationItem } from "data/store/reducers/organization";
import { Logger } from "cms/utils/log/logger";
import { isStringNotEmpty } from "utils/empty-exists";

const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null,
    token: null,
};

const TOKEN = SimpliCityAdminSDK.utils.TOKEN;
const { INVALID, EXPIRED, NEEDS_REFRESH, VALID } = TOKEN;
const AzureADContext = createContext(null);

export const AzureADProvider = (props) => {

    const children = props?.children;
    const [state, dispatch] = useReducer(authReducer, initialState);
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const dispatcher = useDispatch();

    const verifyToken = (serviceToken) => {
        // return false;
        if (!serviceToken) {
            return false;
        }
        try {
            const decoded = jwtDecode(serviceToken);
            return decoded?.exp > Date.now() / 1000;
        } catch (err) {
            Logger.error(err.message);
            return false;
        }
    };

    const verifySession = async () => {
        /* ---------------------------------------------------------
            STATES: [access token / refresh token] :
               - [valid / valid]       -> do nothing
               - [near expiry / valid] -> refresh token
               - [expired / valid]     -> refresh token
               - [expired / expired]   -> logout
          --------------------------------------------------------- */
        try {
            const serviceToken = getCookie("simplicity_admin");
            let isValid = false;
            let status = INVALID;
            const now = Date.now();
            let token_exp;
            // if we have an access token, decoded its expiry
            if (serviceToken) {
                const decoded = jwtDecode(serviceToken);
                token_exp = decoded.exp * 1000;
                const refresh_point = token_exp - 60 * 5 * 1000; // 5 mins before
                if (now > token_exp) {
                    status = EXPIRED;
                    Logger.info(`[AzureADProvider.verifySession] Token: ${EXPIRED}`);
                } else if (now > refresh_point) {
                    status = NEEDS_REFRESH;
                    Logger.info(`[AzureADProvider.verifySession] Token Refresh: ${NEEDS_REFRESH}`);
                } else {
                    status = VALID;
                }
                const nowtime = now / 1000;
                isValid = decoded.exp > nowtime;
            } else {
                Logger.info("[AzureADProvider.verifySession] No Token", { serviceToken });
            }
            // const format = 'h:mm:ss a'
            // const nowFormatted = dayjs(now).format(format)
            // const expiryFormatted = dayjs(token_exp).format(format)

            if (status === NEEDS_REFRESH && isValid) {
                await attemptRefresh();

            } else if (!isValid) {
                dispatch({
                    type: LOGOUT,
                    payload: {
                        isLoggedIn: false,
                        isInitialized: true,
                    },
                });
                dispatcher(removeAccountItem());
                navigate("/Welcome");
                status = false;
                return status;

            } else {
                status = true;
                return status;
            }
        } catch (err) {
            Logger.error(err);
        }
    };

    const setSession = (accessToken, refreshToken) => {
        if (isStringNotEmpty(refreshToken)) {
            localStorage.setItem("simplicity_refresh", refreshToken);
        } else {
            localStorage.removeItem("simplicity_refresh");
        }
        if (accessToken) {
            // Logger.debug("SET ACCESS TOKEN: " + accessToken);
            Logger.debug("[setSession] Set");
            setCookie("simplicity_admin", accessToken, { path: "/", domain: Config.COOKIE_DOMAIN, });
            // TODO test not doing this
            // axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        } else {
            Logger.debug("[setSession] Remove");
            removeCookie("simplicity_admin", { path: "/", domain: Config.COOKIE_DOMAIN, });
            removeCookie("simplicity_refresh", { path: "/", domain: Config.COOKIE_DOMAIN, });
            // delete axios.defaults.headers.common.Authorization;
        }
    };

    // Session Verification Checker
    useEffect(() => {
        const init = async () => {
            try {
                let account;
                const serviceToken = getCookie("simplicity_admin");
                if (serviceToken && verifyToken(serviceToken)) {
            
                    // Get User Profile - Populate login status
                    try {
                        account = await SimpliCityAdminSDK.account.me(serviceToken);
                    } catch (err) {
                        Logger.error(`Request to get profile Failed: ${Config.API_BASEURL}`);
                        Logger.error(err);
                        if (err instanceof AxiosError) {
                            Logger.error(`Network Error - ${err.code} ${err.message}`);
                            dispatch({
                                type: LOGIN,
                                payload: {
                                    isLoggedIn: false,
                                    isInitialized: true
                                },
                            });
                        }
                    }
                    // Populate Roles
                    try {
                        const user_id = account?.guid;
                        const roles = await SimpliCityAdminSDK.role.findRolesForProfile(
                            serviceToken, user_id
                        );
                        dispatcher(setUserRoles(roles));
                    } catch (err) {
                        Logger.error("Request to get roles failed");
                    }

                    if (!account) {
                        dispatch({
                            type: LOGOUT,
                            payload: {
                                isLoggedIn: false,
                                isInitialized: true,
                            },
                        });
                        dispatcher(removeAccountItem());
                        navigate("/Welcome");
                    } else {
                        dispatcher(setAccountItem(account));

                        const org_guid = account.organization;
                        const organization = await SimpliCityAdminSDK.organization.findOne(serviceToken, org_guid);
                        dispatcher(setOrganizationItem(organization));

                        dispatch({
                            type: LOGIN,
                            payload: {
                                isLoggedIn: true,
                                isInitialized: true,
                                user: account,
                                account,
                                token: serviceToken,
                                organization,
                            },
                        });
                    }
                } else {
                    // Login session NOT ok
                    await attemptRefresh();
                }
            } catch (err) {
                Logger.error(`failed to restore tokens. ${err.message}`);
                Logger.error(err);
                console.error(err);
                // Network Error possible if API not responding
                const url = new URL(window.location.href);
                const path = url.pathname;
                if (err && err.name === "AxiosError" && err?.code === "ERR_NETWORK" && path !== "/Welcome") {
                    Logger.error(`Network Error - ${err.code} ${err.message}`);
                    window.location.href = "/Welcome";
                }
                dispatch({
                    type: LOGOUT,
                    payload: {
                        isLoggedIn: false,
                        isInitialized: true,
                    },
                });

            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getExpiresAt = async (decoded_token) => {
        const epochTime = decoded_token.exp;
        const _date = new Date(epochTime * 1000);
        const date = dayjs(_date);
        const formatted_date = date.format(date, Config.dates.display.datetime_short, "America/vancouver");
        return formatted_date;
    };
    
    // ATTEMPT TO REFRESH ACCESS TOKEN VIA REFRESH
    const attemptRefresh = async () => {
        let access_token;
        let refresh_token;
        let status;

        try {
            const old_access_token = getCookie("simplicity_admin");
            const old_refresh_token = localStorage.getItem("simplicity_refresh");
            if (!old_refresh_token) {
                console.error(`[attemptRefresh] Failed token refresh - no refresh token.` );
                dispatch({
                    type: LOGOUT,
                    payload: {
                        isLoggedIn: false,
                        isInitialized: true,
                    },
                });
                removeCookie("simplicity_admin");
                localStorage.removeItem("simplicity_refresh");
                navigate("/Welcome");
                return;
            }
            
            // Verify token via API
            const result = await SimpliCityAdminSDK.account.verifyToken(old_access_token, old_refresh_token);
            
            status = result?.status;
            access_token = result?.access_token;
            refresh_token = result?.refresh_token;
            Logger.info(`[attemptRefresh] ${status} (Refresh token used to renew session)`);
            
        } catch (err) {
            console.error(`[attemptRefresh] Failed token refresh API: ${err.message}` );
            console.error(err);
            removeCookie("simplicity_admin");
            localStorage.removeItem("simplicity_refresh");
            dispatch({
                type: LOGOUT,
                payload: {
                    isLoggedIn: false,
                    isInitialized: true,
                },
            });
            window.location.assign("/Welcome");
        }
        
        try {
            if (access_token) {
                Logger.info("setting cookies");
                if (isStringNotEmpty(access_token)) {
                    const cookieOptions = { path: "/", domain: Config.COOKIE_DOMAIN };
                    setCookie("simplicity_admin", access_token, cookieOptions);
                }
                if (isStringNotEmpty(refresh_token)) {
                    localStorage.setItem(
                        "simplicity_refresh",
                        refresh_token
                    );
                }
                if (status === "success") {
                    Logger.info(`refreshed access: ${status}`);
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                } else {
                    Logger.info("Failed to refresh access token using the refresh token. Redirect to /Welcome.");
                    // REDIRECT TO AZURE LOGOUT
                    dispatch({
                        type: LOGOUT,
                        payload: {
                            isLoggedIn: false,
                            isInitialized: true,
                        },
                    });
                    dispatcher(removeAccountItem());
                    navigate("/Welcome");
                }
            }
        } catch (err) {
            console.error("Failed getting new tokens");
            console.error(err);
            removeCookie("simplicity_admin");
            localStorage.removeItem("simplicity_refresh");
            navigate("/Welcome");
        }
    };
    
    const logout = () => {
        Logger.info("logout taking place");
        removeCookie("simplicity_admin", { path: "/", domain: Config.COOKIE_DOMAIN });
        removeCookie("simplicity_refresh", { path: "/", domain: Config.COOKIE_DOMAIN });
        localStorage.removeItem("simplicity_refresh");
        setSession();
        const url = `${Config.API_BASEURL}${Config.LOGOUT_URL}`;
        Logger.info(`Logout: ${url}`);
        window.location.replace(url);
    };

    const getToken = () => {
        return getCookie("simplicity_admin");
    };

    const requestToken = async () => {
        // Use refresh token to request new access token
        const _access_token = getCookie("simplicity_admin");
        const _refresh_token = localStorage.getItem("simplicity_refresh") ?? getCookie("simplicity_refresh");
        const result = await SimpliCityAdminSDK.account.verifyToken(_access_token, _refresh_token);
        const access_token = result?.access_token;
        const refresh_token = result?.refresh_token;
        setSession(access_token, refresh_token);

        const account = await SimpliCityAdminSDK.account.me(access_token);
        Logger.info("Account: ", account);
        dispatcher(setAccountItem(account));
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                isInitialized: true,
                user: account,
                account,
                token: access_token,
            },
        });
        return access_token;
    };

    const resetPassword = async () => {};
    const updateProfile = () => {};

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <AzureADContext.Provider
            value={{
                ...state,
                logout,
                resetPassword,
                updateProfile,
                verifySession,
                getToken,
                requestToken,
                isAuthenticated,
                attemptRefresh,
            }}
        >
            {children}
        </AzureADContext.Provider>
    );
};

AzureADProvider.propTypes = {
    children: PropTypes.node,
};

export { AzureADContext };
